import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getNotificationTranslations } from '@common/i18n/notifications';
import type { FormLockResponse } from '@common/types/formLocks';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
    extendFormLockStatus,
    getFormLockStatus,
    postContactIdForLocking,
    unlockFormStatus,
    unlockFormStatusQuickly,
} from '@/services/ClientSideTemplateFormService';
import { errorSnackbar } from '@/utils/snackbar';
import { FORM_LOCK_EXTENSION_INTERVAL_MS } from '../timingConfig';
import { extractApiError } from '../types';

export type LockAcquisitionResult =
    | { success: true; contactId: string; lockInfo: FormLockResponse }
    | { success: false; reason: 'locked_by_other'; lockedByEmail: string }
    | { success: false; reason: 'contact_not_found' }
    | { success: false; reason: 'error'; error: unknown };

interface UseFormLockOptions {
    prospectId: string | null;
    prospectVersionId: number | null;
    shouldExtendLock?: boolean;
    /** If true, shows snackbar errors automatically */
    showErrorSnackbars?: boolean;
    language?: TemplateFormLanguage;
}

interface UseFormLockReturn {
    lockInfo: FormLockResponse | null;
    contactId: string;
    isLocked: boolean;
    /**
     * Attempts to acquire the form lock for the given email.
     * Returns a result indicating success or the reason for failure.
     */
    acquireLock: (email: string) => Promise<LockAcquisitionResult>;
    /**
     * Releases the current lock. Safe to call even if no lock is held.
     */
    releaseLock: () => Promise<void>;
    /**
     * Releases the lock synchronously using sendBeacon (for unload handlers).
     * Use this in beforeunload/visibilitychange handlers.
     */
    releaseLockQuickly: () => void;
}

/**
 * Hook to manage form locking for intake forms.
 *
 * Handles:
 * - Acquiring locks by email (get contact ID, then check/acquire lock)
 * - Checking if another user has the lock
 * - Releasing locks manually or on page unload
 * - Auto-cleanup on component unmount
 */
export function useFormLock({
    prospectId,
    prospectVersionId,
    shouldExtendLock = true,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
}: UseFormLockOptions): UseFormLockReturn {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [lockInfo, setLockInfo] = useState<FormLockResponse | null>(null);
    const [contactId, setContactId] = useState<string>('');
    const effectiveProspectId = prospectId || lockInfo?.prospectId || null;

    const releaseLockQuickly = useCallback(() => {
        if (contactId && effectiveProspectId) {
            unlockFormStatusQuickly({
                contactId,
                prospectId: effectiveProspectId,
            });
        }
    }, [contactId, effectiveProspectId]);

    const releaseLock = useCallback(async () => {
        if (!contactId || !effectiveProspectId) return;

        try {
            await unlockFormStatus({ prospectId: effectiveProspectId, contactId });
        } catch (error) {
            console.error('Failed to release form lock:', error);
        }
    }, [contactId, effectiveProspectId]);

    const acquireLock = useCallback(
        async (email: string): Promise<LockAcquisitionResult> => {
            try {
                // Step 1: Get contact ID for the email
                const contactResponse = await postContactIdForLocking({
                    prospectId: prospectId || undefined,
                    contactEmail: email,
                    prospectVersionId: prospectVersionId || undefined,
                });

                const newContactId = contactResponse.data;

                // Step 2: Check/acquire lock status
                const lockResponse = await getFormLockStatus(
                    newContactId,
                    prospectId,
                    prospectVersionId,
                );
                const newLockInfo = lockResponse.data;

                // Step 3: Check if locked by someone else
                const lockedByEmail = newLockInfo?.lockedByEmail || '';
                const isLockedByOther = lockedByEmail.toLowerCase() !== email.toLowerCase();

                if (isLockedByOther && lockedByEmail) {
                    setContactId('');
                    setLockInfo(null);
                    errorSnackbar(
                        `Looks like ${lockedByEmail} is already editing this form. You'll be able to access it once they're done.`,
                    );
                    return {
                        success: false,
                        reason: 'locked_by_other',
                        lockedByEmail,
                    };
                }

                setContactId(newContactId);
                setLockInfo(newLockInfo);

                return {
                    success: true,
                    contactId: newContactId,
                    lockInfo: newLockInfo,
                };
            } catch (error) {
                const apiError = extractApiError(error);

                if (apiError.status === 404) {
                    errorSnackbar(getNotificationTranslations(language).emailNotRecognized);
                    return { success: false, reason: 'contact_not_found' };
                }

                console.error('Failed to acquire form lock:', error);
                return { success: false, reason: 'error', error };
            }
        },
        [prospectId, prospectVersionId, language],
    );

    // Auto-release lock on page unload/visibility change
    useEffect(() => {
        if (!contactId || !effectiveProspectId) return;

        const handleUnlock = () => {
            releaseLockQuickly();
        };

        if (isMobile) {
            const handleVisibility = () => {
                if (document.visibilityState === 'hidden') {
                    handleUnlock();
                }
            };
            document.addEventListener('visibilitychange', handleVisibility);
            window.addEventListener('pagehide', handleUnlock);

            return () => {
                document.removeEventListener('visibilitychange', handleVisibility);
                window.removeEventListener('pagehide', handleUnlock);
            };
        }

        window.addEventListener('beforeunload', handleUnlock);
        return () => {
            window.removeEventListener('beforeunload', handleUnlock);
        };
    }, [isMobile, contactId, effectiveProspectId, releaseLockQuickly]);

    useEffect(() => {
        if (!contactId || !effectiveProspectId || !shouldExtendLock) return;

        const interval = setInterval(() => {
            extendFormLockStatus({ contactId, prospectId: effectiveProspectId });
        }, FORM_LOCK_EXTENSION_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [contactId, effectiveProspectId, shouldExtendLock]);

    return {
        lockInfo,
        contactId,
        isLocked: !!lockInfo,
        acquireLock,
        releaseLock,
        releaseLockQuickly,
    };
}
